export interface SocialLink {
  label: string;
  url: string;
  description: string;
  icon: 'github' | 'linkedin' | 'x' | 'instagram' | 'facebook' | 'network' | 'stackoverflow';
}

export interface StoryChapter {
  title: string;
  period: string;
  text: string;
  image?: string;
  imageAlt?: string;
}

export const SITE = {
  owner: 'Ivan Sivák',
  title: "Ivan Sivák's Personal Workstation",
  location: 'Ostrava, Czech Republic',
  projectsOrigin: (import.meta.env.VITE_PROJECTS_ORIGIN || 'https://apps.ivansivak.com').replace(/\/$/, ''),
  welcome: {
    heading: "Welcome to Ivan Sivák's Personal Workstation",
    lead: 'This is not a résumé. It is a collection of software, artificial-intelligence, graphics, retro-computing, and research experiments—most of them open source, and most of them runnable directly from this website.',
  },
  about: [
    'I am an AI founder and independent researcher from Ostrava, Czech Republic. I have been building software for more than twenty-five years—ever since I became obsessed with programming as a thirteen-year-old, experimenting with OpenGL and 3D graphics and trying to recreate the tools I admired, from Softimage XSI to Maya. I am a first-principles person by nature, and I have always read widely in theoretical physics, neuroscience, and philosophy. That early fascination with building complex systems is still what drives me today.',
    'Over the years I have worked across many layers of software engineering: from graphics and low-level technical experimentation to enterprise systems, product development, and machine-learning and AI platforms. My recent work centers on agentic AI and language models, where I blend neuroscience-inspired ideas with practical, scalable systems.',
    'In 2018, well before the GPT revolution, I founded BotX, a no-code platform for building intelligent AI agents, and led it as Founder, CEO, and CTO. BotX was acquired by Synthreo, where I then served as Co-Founder and CTO. Right now I’m working in stealth mode on a new AI startup lab focused on the next generation of AI-native software and the new paradigms it will bring — and I remain the same curious engineer who wants to understand how intelligence and the mind actually work.',
  ],

  socialLinks: [
    {
      label: 'GitHub',
      url: 'https://github.com/ivansivak86',
      description: 'Source code, experiments, and open-source projects',
      icon: 'github',
    },
    {
      label: 'LinkedIn',
      url: 'https://www.linkedin.com/in/ivan-sivak',
      description: 'Professional history and occasional project notes',
      icon: 'linkedin',
    },
    {
      label: 'X',
      url: 'https://x.com/IvanSivakX',
      description: 'Project experiments, engineering notes, and assorted thoughts',
      icon: 'x',
    },
    {
      label: 'Instagram',
      url: 'https://www.instagram.com/ivansivak.xsi/',
      description: 'Photographs, visual notes, and occasional project updates',
      icon: 'instagram',
    },
    {
      label: 'Facebook',
      url: 'https://www.facebook.com/ivan.sivak.jr',
      description: 'Personal profile and occasional public updates',
      icon: 'facebook',
    },
    {
      label: 'Stack Overflow',
      url: 'https://stackoverflow.com/users/2717169/ivan-sivak',
      description: 'A long trail of technical questions and answers',
      icon: 'stackoverflow',
    },
  ] satisfies SocialLink[],
  story: [
     {
      period: '1990s–2000s',
      title: 'The 13-year-old who could not stop building',
      text: 'I started programming at thirteen, drawn in by OpenGL, 3D graphics, and a desire to recreate the tools I admired—Softimage XSI, Maya. Ray tracers, scene editors, and small engines became my way of learning mathematics and software architecture by doing. I finished elementary school at Pujmanova in Havířov and then studied Information Technology at the Technical School Sykorová in Ostrava (2002–2006).',
     },
     {
      period: '2006–2013',
      title: 'Desktop and enterprise systems',
      text: 'My career began at CruxIT (2006–2008), where I wrote Delphi and PL/SQL and built a desktop CMS used by TV providers. I then joined HSBC as a Senior Software Engineer (2008–2013), designing, developing, testing, and releasing a large ASP.NET / VB.NET application for internal business clients. It was here I learned to make complicated, real-world systems reliable and maintainable.',
     },
     {
      period: '2014–2018',
      title: 'Retail web engineering at scale',
      text: 'At Wincor Nixdorf I became Lead Software Engineer (2014–2018), leading web development for retail applications. The work was rich, responsive JavaScript—AngularJS and vanilla JS with Gulp—backed by RESTful C# WebAPI services over SQL Server in a clean N-Tier architecture, fully responsive across every device. This is where I first shipped products for real users at scale.',
     },
     {
      period: '2015–2018',
      title: 'From the mind to the machine',
      text: 'Around 2015, a personal experience quietly redirected the direction of my curiosity. I had been in a relationship that lasted about a year; after we parted, I learned that the person I had loved was living with schizophrenia. That discovery drew me toward neuroscience—part genuine curiosity, part a quiet, personal way of trying to understand something I had witnessed. I was most at home in molecular neuroscience, because the mechanistic, first-principles way it describes how molecules and cells give rise to complex behaviour resonated with the systems thinking I already knew from computer engineering. From 2015 on, that fascination with how minds work became a fascination with how machines might learn to think, and it carried me into machine learning and artificial intelligence, where I have worked ever since.',
     },
     {
      period: '2018–2025',
      title: 'Founding BotX',
      text: 'In 2018—before generative AI made headlines—I founded BotX, a no-code platform that lets companies create intelligent AI agents: automated workflows powered by machine learning and, later, generative AI. I led it as Founder, CEO, and CTO. Its vision turned out to coincide perfectly with the progress of generative AI and enterprise adoption, and BotX was acquired by Synthreo.',
     },
     {
      period: '2025–present',
      title: 'Synthreo, then independent research',
      text: 'After the acquisition I joined Synthreo as Co-Founder and CTO (2025–2026), building edge AI agent solutions that automate routine tasks, sharpen decision-making, and create seamless collaboration between human teams and digital labor. Today I’m working in stealth mode on a new AI startup lab, exploring the next generation of AI-native software and the paradigms it opens — what comes after chat-shaped AI. Alongside that, I keep studying at the edges of my curiosity: a Columbia MicroMasters in Artificial Intelligence, and ongoing work in molecular neuroscience, computer security, aerospace, and biology in code.',
     },
  ] satisfies StoryChapter[],


} as const;
